import { clsx } from '../../../ds/clsx';
import { Button, Eyebrow, Skeleton, Tooltip } from '../../../ds/components';
import type { DocumentRow } from '../types';
import { Badge, EmptyBox } from './bits';
import styles from './record.module.css';

export interface DocumentsCardProps {
  rows: DocumentRow[] | null;
  loading: boolean;
  /** The empty text of the state (I.09 duplicate, DNC); null = the generic one. */
  emptyText: string | null;
  onOpenAll: () => void;
  onOpenRow: (row: DocumentRow) => void;
  /** „Hochladen\" in the card head (D-443): opens the upload dialog in every state of the card. */
  onUpload: () => void;
  /** Why uploading is locked (undecided duplicate, GDPR deletion) — the button stays visible, dimmed with the reason. */
  uploadLockedReason?: string | null | undefined;
}

/** Generic empty text of the card (D-443, Paul 04.11.2026): no lifecycle condition, upload and request are always possible. */
export const DOCUMENTS_EMPTY_TEXT = 'Laden Sie Unterlagen hoch oder fordern Sie sie beim Kontakt an.';

function filesText(count: number): string {
  return count === 1 ? '1 Datei' : `${count} Dateien`;
}

/** The record's „Dokumente\" card: upload, the three latest rows and the jump into the browser (I.01–I.11, D-443). */
export function DocumentsCard({
  rows,
  loading,
  emptyText,
  onOpenAll,
  onOpenRow,
  onUpload,
  uploadLockedReason = null,
}: DocumentsCardProps) {
  const count = rows?.length ?? 0;
  const recent = rows?.slice(0, 3) ?? [];
  const locked = uploadLockedReason !== null;
  const uploadButton = (
    <Button
      variant="secondary"
      size="small"
      aria-disabled={locked || undefined}
      onClick={locked ? undefined : onUpload}
    >
      Hochladen
    </Button>
  );
  return (
    <section className={styles.card} aria-label="Dokumente">
      <div className={clsx(styles.cardHead, styles.cardHeadCenter)}>
        <Eyebrow size={11}>Dokumente</Eyebrow>
        <span className={styles.cardHeadActions}>
          <span className={styles.cardCount}>
            {loading ? <Skeleton width={60} height={10} /> : count > 0 ? filesText(count) : 'keine'}
          </span>
          {locked ? <Tooltip content={uploadLockedReason}>{uploadButton}</Tooltip> : uploadButton}
        </span>
      </div>
      {loading ? (
        <div className={styles.docRows}>
          {[0, 1, 2].map((index) => (
            <div key={index} className={styles.docRow} style={{ cursor: 'default' }}>
              <span className={styles.docText}>
                <Skeleton width="70%" />
                <Skeleton width="45%" height={8} style={{ marginTop: 6 }} />
              </span>
            </div>
          ))}
        </div>
      ) : count === 0 ? (
        <EmptyBox title="Noch keine Dokumente." text={emptyText ?? DOCUMENTS_EMPTY_TEXT} />
      ) : (
        <div className={styles.docRows}>
          {recent.map((row) => (
            <button key={row.id} type="button" className={styles.docRow} onClick={() => onOpenRow(row)}>
              <span className={styles.docText}>
                <span className={styles.docName}>{row.name}</span>
                <span className={styles.docMeta}>{row.meta}</span>
              </span>
              <Badge tone={row.tone}>{row.statusLabel}</Badge>
            </button>
          ))}
          <button type="button" className={styles.docAll} onClick={onOpenAll}>
            <span className={styles.docAllLabel}>Alle anschauen</span>
            <span className={styles.docAllRest}>
              {count > 3 ? `+ ${count - 3} weitere` : 'vollständige Liste'}
            </span>
            <span className={styles.docAllArrow} aria-hidden="true">
              →
            </span>
          </button>
        </div>
      )}
    </section>
  );
}
